import * as React from "react";
import { Route, Routes, useMatch, useNavigate, useParams } from "react-router-dom";
import { NotFoundPage } from "../Error/NotFoundPage";
import { LoadingPage } from "../LoadingPage";
import { getExpressData } from "./express-data";
import { PasswordSetupPage } from "./PasswordSetupPage";
import { ExpressFormSubmittedPage } from "./ExpressFormSubmittedPage";
import { ExpressKYCPage } from "./ExpressKYCPage";
import { ServiceProvider } from "@/types/ServiceProvider";
import { useServiceProvider } from "@/hooks/useServiceProvider";
import {
  GetAccountsComponent,
  GetNotifierComponent,
  GetExecutorComponent,
  GetNokComponent,
  GetDocumentsComponent,
  GetKYCComponent,
  GetSubmitComponent,
  GetAdditionalServicesComponent,
  NotSubmittedPage,
  ExpressComponentProps,
} from "./ExpressFormComponents";
import { ExpressUiInfoContext } from "@/store/ExpressUiInfo";
import { isQuilter } from "@/helpers/service-provider-helpers";
import { calcFormMenus, collectDocuments, isExpressKycReq } from "@/helpers/express-helpers";

export const ExpressFormPage: React.FC = () => {
  const { slug } = useParams() as { slug: string };

  const { loading, error, serviceProvider } = useServiceProvider(slug);

  if (loading) return <LoadingPage />;
  if (error) return <NotFoundPage />;
  if (!serviceProvider) return <NotFoundPage />;
  return <Router sp={serviceProvider} />;
};

const Router: React.FC<{
  sp: ServiceProvider;
}> = ({ sp: serviceProvider }) => {
  const match = useMatch("/express/:slug/*");
  if (!match) throw new Error("Unexpected");
  const navigate = useNavigate();

  const [count, setCount] = React.useState(0);
  const { state: uiInfo, dispatch: uiDispatch } = React.useContext(ExpressUiInfoContext);

  React.useEffect(() => {
    uiDispatch({ type: "SET_QUILTER", payload: isQuilter(serviceProvider.id) });
  }, [serviceProvider]);
  const refresh = React.useCallback(() => setCount((c) => c + 1), [setCount]);
  const submitted = React.useMemo(() => getExpressData()?.submitted, []);
  const formMenuEntries = React.useMemo(
    () => calcFormMenus(serviceProvider, uiInfo),
    [serviceProvider, uiInfo, count]
  );

  const onSectionClick = React.useCallback(
    (section) => {
      refresh();
      navigate(`${match.pathnameBase}/${section}`);
    },
    [match.pathnameBase, navigate, refresh]
  );

  const expressComponentProps: ExpressComponentProps = {
    serviceProvider,
    formMenuEntries,
    match,
    refresh,
    submitted,
    navigate,
    onSectionClick,
    collectExecutorDetails: uiInfo.collectExecutorDetails,
    collectNokDetails: uiInfo.collectNokDetails,
    willExists: uiInfo.willExists,
    isCollectDocuments: collectDocuments(serviceProvider, uiInfo),
    isKyc: isExpressKycReq(serviceProvider),
  };

  return (
    <Routes>
      <Route path="account" element={GetAccountsComponent(expressComponentProps)} />
      <Route path="notifier" element={GetNotifierComponent(expressComponentProps)} />

      <Route path="executor" element={GetExecutorComponent(expressComponentProps)} />
      <Route path="nok" element={GetNokComponent(expressComponentProps)} />

      <Route path="documents" element={GetDocumentsComponent(expressComponentProps)} />
      <Route path="kyc" element={GetKYCComponent(expressComponentProps)} />
      <Route path="verify-identity" element={<ExpressKYCPage />} />
      <Route path="submit" element={GetSubmitComponent(expressComponentProps)} />

      <Route
        path="submitted"
        element={
          submitted ? (
            <ExpressFormSubmittedPage serviceProvider={serviceProvider} />
          ) : (
            NotSubmittedPage({ match })
          )
        }
      />

      <Route path="password" element={<PasswordSetupPage nextPageURL="/form" />} />

      <Route path="/" element={<Root />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const Root = () => {
  const match = useMatch("/express/:slug/*");
  const navigate = useNavigate();

  React.useEffect(() => {
    if (match) {
      navigate(`${match.pathnameBase}/account`);
    }
  }, [match, navigate]);

  return null;
};
